import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, FileText, Link2, Mail, Plus, RefreshCw, Save, Send, Trash2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { resolveMedia } from "@/lib/api";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
const SL_LABELS = { bozza: "Bozza", generata: "Generata", inviata: "Inviata", visualizzata: "Visualizzata", in_acquisto: "In acquisto", acquistata: "Acquistata" };
const EVENT_LABELS = { viewed: "Shopping List visualizzata", product_viewed: "Prodotto visualizzato", add_to_cart: "Aggiunta al carrello", purchased: "Acquistato" };
const emptyItem = { product_id: "", variant: "", finish: "", quantity: 1, room: "", note: "" };

function ItemEditor({ item, products, onChange, onRemove, idx }) {
  const p = products.find((x) => x.id === item.product_id);
  return (
    <div className="border border-hair p-4 space-y-3" data-testid={`sl-item-${idx}`}>
      <div className="grid md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Prodotto *</Label>
          <Select value={item.product_id} onValueChange={(v) => onChange({ ...item, product_id: v, variant: "" })}>
            <SelectTrigger className="rounded-none mt-1" data-testid={`sl-product-${idx}`}><SelectValue placeholder="Cerca nel catalogo…" /></SelectTrigger>
            <SelectContent>
              {products.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}{x.brand ? ` · ${x.brand}` : ""} — {eur(x.price)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Quantità</Label>
          <Input type="number" min={1} value={item.quantity} onChange={(e) => onChange({ ...item, quantity: Math.max(1, parseInt(e.target.value) || 1) })} className="rounded-none mt-1" data-testid={`sl-qty-${idx}`} />
        </div>
      </div>
      {(p?.variants || []).length > 0 && (
        <div>
          <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Variante</Label>
          <Select value={item.variant || "__base__"} onValueChange={(v) => onChange({ ...item, variant: v === "__base__" ? "" : v })}>
            <SelectTrigger className="rounded-none mt-1" data-testid={`sl-variant-${idx}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__base__">Base</SelectItem>
              {(p.variants || []).map((v) => <SelectItem key={v.name} value={v.name}>{v.name}{v.price != null ? ` — ${eur(v.price)}` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="grid md:grid-cols-3 gap-3">
        <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Finitura</Label>
          <Input value={item.finish} onChange={(e) => onChange({ ...item, finish: e.target.value })} className="rounded-none mt-1" data-testid={`sl-finish-${idx}`} /></div>
        <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Ambiente</Label>
          <Input value={item.room} onChange={(e) => onChange({ ...item, room: e.target.value })} placeholder="es. Soggiorno" className="rounded-none mt-1" data-testid={`sl-room-${idx}`} /></div>
        <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Nota progettista</Label>
          <Input value={item.note} onChange={(e) => onChange({ ...item, note: e.target.value })} className="rounded-none mt-1" data-testid={`sl-note-${idx}`} /></div>
      </div>
      <button type="button" onClick={onRemove} className="text-xs text-red-700 hover:underline min-h-[44px]" data-testid={`sl-remove-${idx}`}>Rimuovi articolo</button>
    </div>
  );
}

export function ShoppingListsPanel() {
  const [lists, setLists] = useState([]);
  const [products, setProducts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [view, setView] = useState("lists"); // lists | requests
  const [sel, setSel] = useState(null); // null | "new" | sl list item
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [sl, pr, cr, rq] = await Promise.all([
        api.get("/admin/shopping-lists"), api.get("/admin/products"), api.get("/admin/crm/contacts", { params: { limit: 500 } }),
        api.get("/admin/shop-the-project/requests"),
      ]);
      setLists(sl.data); setProducts(pr.data.filter((p) => p.published !== false)); setContacts(cr.data.items); setRequests(rq.data);
    } catch { toast.error("Errore caricamento Shop the Project"); }
  };
  useEffect(() => { load(); }, []);

  const setReqStatus = async (id, status) => {
    try { await api.patch(`/admin/shop-the-project/requests/${id}`, { status }); toast.success("Stato aggiornato"); load(); }
    catch { toast.error("Errore aggiornamento"); }
  };

  const open = async (l) => {
    setSel(l);
    try {
      const { data } = await api.get(`/admin/shopping-lists/${l.id}`);
      setDetail(data);
      setForm({ contact_email: data.contact_email, contact_name: data.contact_name, project_name: data.project_name,
                service_key: data.service_key || "", notes_internal: data.notes_internal || "",
                items: data.items.map((i) => ({ ...emptyItem, ...i })) });
    } catch { toast.error("Errore caricamento"); }
  };

  const save = async () => {
    if (!form.project_name.trim()) { toast.error("Nome progetto obbligatorio"); return; }
    if (!form.items.length || form.items.some((i) => !i.product_id)) { toast.error("Aggiungi almeno un prodotto valido"); return; }
    setBusy(true);
    try {
      if (sel === "new") {
        const { data } = await api.post("/admin/shopping-lists", form);
        toast.success("Shopping List creata");
        setSel({ id: data.id }); await load(); await open({ id: data.id });
      } else {
        await api.put(`/admin/shopping-lists/${sel.id}`, form);
        toast.success("Shopping List aggiornata"); load();
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Errore salvataggio"); }
    finally { setBusy(false); }
  };

  const copyLink = (link) => {
    try {
      navigator.clipboard?.writeText(link).then(
        () => toast.success("Link cliente copiato negli appunti"),
        () => { window.prompt("Copia manualmente il link cliente:", link); },
      );
    } catch { window.prompt("Copia manualmente il link cliente:", link); }
  };

  const act = async (action, label) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/shopping-lists/${sel.id}/${action}`);
      toast.success(label);
      if (data?.link) copyLink(data.link);
      await open(sel); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Operazione non riuscita"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Eliminare definitivamente questa Shopping List?")) return;
    try { await api.delete(`/admin/shopping-lists/${sel.id}`); toast.success("Eliminata"); setSel(null); setDetail(null); load(); }
    catch { toast.error("Errore eliminazione"); }
  };

  const btn = "min-h-[44px] inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase px-4 py-2 transition-colors disabled:opacity-60";

  // ---------- Editor / dettaglio ----------
  if (sel && form) {
    const d = detail;
    return (
      <div className="space-y-6" data-testid="sl-editor">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <button onClick={() => { setSel(null); setDetail(null); }} className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid="sl-back"><ArrowLeft size={14} /> Tutte le Shopping List</button>
          {d && <span className="text-[10px] tracking-[0.14em] uppercase border border-gold/50 text-gold px-3 py-1.5" data-testid="sl-status">{SL_LABELS[d.status] || d.status}</span>}
        </div>

        <div className="grid lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-2 bg-white border border-hair p-5 md:p-6 space-y-5">
            <div className="eyebrow">Progetto e cliente</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Cliente (dal CRM) *</Label>
                <Select value={form.contact_email} onValueChange={(v) => { const c = contacts.find((x) => x.email === v); setForm({ ...form, contact_email: v, contact_name: c ? [c.name, c.surname].filter(Boolean).join(" ") : form.contact_name }); }}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="sl-contact"><SelectValue placeholder="Seleziona contatto…" /></SelectTrigger>
                  <SelectContent>
                    {contacts.map((c) => <SelectItem key={c.id} value={c.email}>{[c.name, c.surname].filter(Boolean).join(" ") || c.email} · {c.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Nome progetto *</Label>
                <Input value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} placeholder="es. Casa Rossi — Soggiorno" className="rounded-none mt-1" data-testid="sl-project-name" /></div>
            </div>
            <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Servizio associato</Label>
              <Select value={form.service_key || "none"} onValueChange={(v) => setForm({ ...form, service_key: v === "none" ? "" : v })}>
                <SelectTrigger className="rounded-none mt-1" data-testid="sl-service"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno</SelectItem>
                  <SelectItem value="essenza">Essenza</SelectItem>
                  <SelectItem value="atelier">Atelier</SelectItem>
                  <SelectItem value="maison">Maison</SelectItem>
                  <SelectItem value="autonomo">Servizio autonomo</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="eyebrow pt-2">Articoli selezionati</div>
            {form.items.map((it, i) => (
              <ItemEditor key={i} idx={i} item={it} products={products}
                onChange={(ni) => setForm({ ...form, items: form.items.map((x, j) => (j === i ? ni : x)) })}
                onRemove={() => setForm({ ...form, items: form.items.filter((_, j) => j !== i) })} />
            ))}
            <button type="button" onClick={() => setForm({ ...form, items: [...form.items, { ...emptyItem }] })} className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid="sl-add-item"><Plus size={14} /> Aggiungi articolo</button>

            <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Note interne</Label>
              <Textarea rows={2} value={form.notes_internal} onChange={(e) => setForm({ ...form, notes_internal: e.target.value })} className="rounded-none mt-1" data-testid="sl-notes" /></div>
            <button onClick={save} disabled={busy} className={`${btn} bg-ink text-bone hover:bg-gold`} data-testid="sl-save"><Save size={14} /> Salva</button>
          </div>

          {d && (
            <div className="space-y-4">
              <div className="bg-white border border-hair p-5" data-testid="sl-side">
                <div className="eyebrow mb-3">Totali attuali (catalogo live)</div>
                <dl className="text-sm space-y-1.5">
                  <div className="flex justify-between"><dt className="text-taupe">Listino</dt><dd>{eur(d.totals.listino)}</dd></div>
                  <div className="flex justify-between text-gold"><dt>Vantaggio cliente</dt><dd>−{eur(d.totals.vantaggio)}</dd></div>
                  <div className="flex justify-between font-medium border-t border-hair pt-2"><dt>Shop the Project</dt><dd>{eur(d.totals.riservato)}</dd></div>
                </dl>
                <div className="mt-4 flex flex-col gap-2">
                  <button onClick={() => act("generate", "PDF generato")} disabled={busy} className={`${btn} bg-ink text-bone hover:bg-gold justify-center`} data-testid="sl-generate"><FileText size={14} /> Genera Shopping List (PDF)</button>
                  {d.pdf_url && <a href={resolveMedia(d.pdf_url)} target="_blank" rel="noopener" className={`${btn} border border-ink hover:bg-ink hover:text-bone justify-center`} data-testid="sl-open-pdf"><FileText size={14} /> Apri PDF (v{(d.versions || []).length})</a>}
                  <button onClick={() => act("send", "Email inviata al cliente")} disabled={busy || !d.pdf_url} className={`${btn} border border-ink hover:bg-ink hover:text-bone justify-center`} data-testid="sl-send"><Send size={14} /> Invia al cliente</button>
                  <button onClick={() => copyLink(`${window.location.origin}/progetto/${d.access_key}`)} className={`${btn} border border-hair hover:border-ink justify-center`} data-testid="sl-copy-link"><Link2 size={14} /> Copia link cliente</button>
                  {d.revoked
                    ? <button onClick={() => act("renew", "Accesso rinnovato")} disabled={busy} className={`${btn} border border-emerald-700 text-emerald-700 hover:bg-emerald-700 hover:text-bone justify-center`} data-testid="sl-renew"><RefreshCw size={14} /> Riattiva accesso</button>
                    : <button onClick={() => window.confirm("Revocare l'accesso del cliente? Il link smetterà di funzionare.") && act("revoke", "Accesso revocato")} disabled={busy} className={`${btn} border border-red-700 text-red-700 hover:bg-red-700 hover:text-bone justify-center`} data-testid="sl-revoke"><X size={14} /> Revoca accesso</button>}
                  <button onClick={remove} className={`${btn} text-red-700 justify-center`} data-testid="sl-delete"><Trash2 size={14} /> Elimina</button>
                </div>
              </div>

              <div className="bg-white border border-hair p-5" data-testid="sl-tracking">
                <div className="eyebrow mb-3">Tracking</div>
                <dl className="text-xs text-taupe space-y-1.5">
                  <div className="flex justify-between"><dt>Generata</dt><dd>{(d.versions || []).length ? fmtDate(d.versions[d.versions.length - 1].generated_at) : "—"}</dd></div>
                  <div className="flex justify-between"><dt>Inviata</dt><dd>{fmtDate(d.sent_at)}</dd></div>
                  <div className="flex justify-between"><dt>Visualizzata</dt><dd>{fmtDate(d.viewed_at)}</dd></div>
                  <div className="flex justify-between"><dt>Scadenza accesso</dt><dd>{fmtDate(d.expires_at)}</dd></div>
                </dl>
                <div className="mt-3 border-t border-hair pt-3 space-y-1.5">
                  {(d.events || []).slice().reverse().slice(0, 12).map((e, i) => (
                    <p key={i} className="text-[11px] text-taupe">{fmtDate(e.at)} — {EVENT_LABELS[e.event] || e.event}</p>
                  ))}
                  {!(d.events || []).length && <p className="text-[11px] text-taupe">Nessun evento registrato.</p>}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---------- Lista ----------
  const REQ_LABELS = { in_attesa_pagamento: "In attesa di pagamento", quotazione_richiesta: "Quotazione richiesta", nuovo: "Nuovo Shop the Project", nuova: "Nuovo Shop the Project", brief_ricevuto: "Brief ricevuto", in_selezione: "In selezione", sl_in_preparazione: "Shopping List in preparazione", sl_inviata: "Shopping List inviata", shopping_list_creata: "Shopping List creata", revisione: "Revisione", approvata: "Approvata", acquisti_in_corso: "Acquisti in corso", completato: "Completato", in_lavorazione: "In lavorazione", chiusa: "Chiusa" };
  return (
    <div className="space-y-5" data-testid="sl-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2">
          <button onClick={() => setView("lists")} className={`${btn} ${view === "lists" ? "bg-ink text-bone" : "border border-hair hover:border-ink"}`} data-testid="stp-view-lists">Shopping List ({lists.length})</button>
          <button onClick={() => setView("requests")} className={`${btn} ${view === "requests" ? "bg-ink text-bone" : "border border-hair hover:border-ink"}`} data-testid="stp-view-requests">Richieste ({requests.filter((r) => r.status === "nuova").length} nuove)</button>
        </div>
        <button onClick={() => { setSel("new"); setDetail(null); setForm({ contact_email: "", contact_name: "", project_name: "", service_key: "", notes_internal: "", items: [{ ...emptyItem }] }); }} className={`${btn} bg-ink text-bone hover:bg-gold`} data-testid="sl-new"><Plus size={14} /> Nuova Shopping List</button>
      </div>

      {view === "requests" ? (
        <div className="space-y-3" data-testid="stp-requests">
          {requests.length === 0 && <div className="p-12 text-center text-taupe bg-white border border-hair">Nessuna richiesta Shop the Project. Le richieste dalla landing /shop-the-project appaiono qui.</div>}
          {requests.map((r) => (
            <div key={r.id} className="bg-white border border-hair p-4 md:p-5" data-testid={`stp-req-${r.id}`}>
              <div className="flex justify-between items-start gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-serif text-lg">{[r.name, r.surname].filter(Boolean).join(" ")}</span>
                    {r.surface_label && <span className="text-[10px] tracking-[0.12em] uppercase border border-hair text-taupe px-2 py-0.5">{r.surface_label}</span>}
                    {r.service_price != null && <span className="text-[10px] tracking-[0.12em] uppercase border border-gold/50 text-gold px-2 py-0.5">{eur(r.service_price)}</span>}
                    {r.payment_status === "paid" && <span className="text-[10px] tracking-[0.12em] uppercase border border-emerald-700/40 text-emerald-700 px-2 py-0.5">Pagato{r.order_number ? ` · ${r.order_number}` : ""}</span>}
                    {r.payment_status === "pending" && r.kind === "online_order" && <span className="text-[10px] tracking-[0.12em] uppercase border border-amber-700/40 text-amber-700 px-2 py-0.5">Pagamento in attesa</span>}
                  </div>
                  <p className="text-sm text-taupe mt-1">{r.email}{r.phone ? ` · ${r.phone}` : ""}</p>
                  <p className="text-sm text-taupe mt-0.5">
                    {r.environments?.length ? r.environments.join(", ") : r.room_type || "—"}
                    {r.budget ? ` · Budget ${r.budget}` : ""}{r.timeline ? ` · ${r.timeline}` : ""}{r.sqm ? ` · ${r.sqm}` : ""}
                  </p>
                  {r.brief && (r.brief.style || r.brief.needs || r.brief.colors || r.brief.keep_existing || r.brief.notes) && (
                    <div className="text-xs text-taupe mt-2 border-l-2 border-gold/40 pl-3 space-y-1" data-testid={`stp-req-brief-${r.id}`}>
                      {r.brief.style && <p><strong className="text-ink/70">Stile:</strong> {r.brief.style}</p>}
                      {r.brief.needs && <p><strong className="text-ink/70">Esigenze:</strong> {r.brief.needs}</p>}
                      {r.brief.colors && <p><strong className="text-ink/70">Colori/materiali:</strong> {r.brief.colors}</p>}
                      {r.brief.keep_existing && <p><strong className="text-ink/70">Da mantenere:</strong> {r.brief.keep_existing}</p>}
                      {r.brief.notes && <p><strong className="text-ink/70">Note:</strong> {r.brief.notes}</p>}
                    </div>
                  )}
                  {(r.attachments || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {r.attachments.map((u, i) => (
                        <a key={i} href={resolveMedia(u)} target="_blank" rel="noopener" className="text-[10px] tracking-[0.1em] uppercase border border-hair px-2 py-1 hover:border-gold text-taupe" data-testid={`stp-req-file-${r.id}-${i}`}>Allegato {i + 1}</a>
                      ))}
                    </div>
                  )}
                  {r.message && <p className="text-xs text-taupe italic mt-2 border-l-2 border-gold/40 pl-3">{r.message}</p>}
                  {r.marketing_consent && <p className="text-[10px] tracking-[0.12em] uppercase text-emerald-700 mt-2">Consenso marketing</p>}
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <p className="text-xs text-taupe">{fmtDate(r.created_at)}</p>
                  <Select value={r.status} onValueChange={(v) => setReqStatus(r.id, v)}>
                    <SelectTrigger className="rounded-none h-8 w-56 text-xs" data-testid={`stp-req-status-${r.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(REQ_LABELS).filter(([k]) => !["nuova", "in_lavorazione", "shopping_list_creata", "chiusa"].includes(k)).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
      <div className="space-y-3" data-testid="sl-list">
        {lists.length === 0 && <div className="p-12 text-center text-taupe bg-white border border-hair">Nessuna Shopping List. Crea la prima da un contatto CRM.</div>}
        {lists.map((l) => (
          <button key={l.id} onClick={() => open(l)} className="w-full text-left bg-white border border-hair p-4 md:p-5 hover:border-gold transition-colors" data-testid={`sl-row-${l.id}`}>
            <div className="flex justify-between items-start gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-serif text-lg">{l.project_name}</span>
                  <span className="text-[10px] tracking-[0.14em] uppercase border border-gold/50 text-gold px-2 py-0.5">{SL_LABELS[l.status] || l.status}</span>
                  {l.revoked && <span className="text-[10px] tracking-[0.14em] uppercase border border-red-700/40 text-red-700 px-2 py-0.5">Revocata</span>}
                </div>
                <p className="text-sm text-taupe mt-1">{l.contact_name || l.contact_email} · {(l.items || []).length} articoli · potenziale {eur(l.totals?.riservato)} · acquistato {eur(l.purchased_value)}</p>
              </div>
              <p className="text-xs text-taupe shrink-0">{fmtDate(l.created_at)}</p>
            </div>
          </button>
        ))}
      </div>
      )}
    </div>
  );
}
